import { adminLocate } from '@/config/adminNav'

// Route guard for every admin surface, driven by config/adminNav.js.
//
// Before this there was NO admin route middleware in the app at all:
// default.global.js checks for a token and rewrites report URLs, nothing else.
// AdminRail.vue and AdminHomeBox.vue do filter the nav by permission, but a
// hidden link is not a gate — all 38 pages under /admin stayed reachable by
// typing the URL, along with every Generate, Run and Judge button on them.
//
// ⚠ GLOBAL, not a per-page `definePageMeta`, and that is the point: 30 of the
// 38 admin pages already carry a definePageMeta in four different shapes, so
// editing each one was both risky and forgettable. A new admin page inherits
// this guard instead of having to remember it.
//
// ⚠ THE PERMISSION COMES FROM THE NAV ENTRY, never a blanket admin:read, so
// the page and the link that points at it can never disagree about who may
// open it. Most entries are admin:read, but the tree also carries users:read,
// settings:read, company:write, prompts:read/write, models:read, sources:read,
// site_feedback:read, email_templates:read and mcp:manage — a blanket gate
// would lock a Manager out of pages the nav offers them.
//
// Fails CLOSED twice over: a route with no nav entry falls back to admin:read
// (today only /admin/alert-feedback), and a failed permissions load leaves
// can() false, which sends the user home.
export default defineNuxtRouteMiddleware(async (to) => {
  // Permissions live in a client-side store; there is nothing to check in SSR.
  if (import.meta.server) return

  const located = adminLocate(to.path)
  const isAdminSurface = to.path.startsWith('/admin') || to.path === '/dashboard/admin'
  if (!located && !isAdminSurface) return

  // An unauthenticated visitor is default.global.js's job — it sends them to
  // signin, which is a better destination than home. Bail rather than race it.
  const { hasToken } = useAuth()
  if (!hasToken.value) return

  const { can, loadPermissions } = usePermissions()
  // The store can be empty on a hard navigation or a refresh, so load before
  // asking. loadPermissions caches, so this is free on every later hop.
  await loadPermissions()

  const needed = located?.item?.permission || located?.group?.permission || 'admin:read'
  if (!can(needed)) return navigateTo('/')
})
